import qs from 'qs'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { VideoApi } from 'src/api'
import { axiosHiringApi } from 'src/api/axios'
import { Spinner, useAnalytic } from 'src/components'
import { ETrackingEvent, EVideoPrivacy } from 'src/enums'
import { useBehaviorMapper, useDebouncedLoading } from 'src/hooks'
import { IVideoModel } from 'src/interfaces'
import { VVCCard } from 'src/partials/card/vvc-card'
import { ModalRecorder } from 'src/partials/recorder/modal-recorder'
import { DialogService, LoadMoreService } from 'src/services'
import { AuthModule } from 'src/store'

interface IProps {
  keyword?: string
  screen?: string
  onUpdateProfile?: (videoId: number, fnCallback?: () => void) => void
}

export const VideoPublished: FC<IProps> = ({ keyword, screen, onUpdateProfile }) => {
  const { analytic } = useAnalytic(screen)
  const profile = useBehaviorMapper(AuthModule.profile$)
  const isCompanyVerified = useBehaviorMapper(AuthModule.verifiedCompany$)

  const _loadMoreService = useMemo(() => new LoadMoreService<IVideoModel & { usedForCampaigns?: number }>({
    axiosInstance: axiosHiringApi,
    endpoint: VideoApi._prefix + '?' + qs.stringify({
      keyword,
      privacy: EVideoPrivacy.PUBLIC
    })
  }), [keyword, profile?.pfvId]) // Need to watch profile?.pfvId to refresh when user update pfv

  const { ref: refLoadMore } = useInView({ threshold: 0 })
  const loading = useBehaviorMapper(_loadMoreService.loading$)
  const items = useBehaviorMapper(_loadMoreService.items$)

  const onRecordVideo = useCallback(() => {
    analytic(ETrackingEvent.BTN_RECORD_VVC)
    DialogService.open(ModalRecorder, {
      forProfile: true,
      requireCompanyVerified: !isCompanyVerified,
      onClose: (result) => {
        result?.video?.id && onUpdateProfile?.(result?.video?.id, () => {
          _loadMoreService.refresh()
        })
      }
    })
  }, [analytic, isCompanyVerified, onUpdateProfile, _loadMoreService])

  useEffect(() => {
    _loadMoreService.loadMore()
  }, [_loadMoreService])

  const debounceLoading = useDebouncedLoading(!items.length && loading)

  if (debounceLoading) {
    return (
      <div className="fx-1 fx fx-center">
        <Spinner/>
      </div>
    )
  }

  return (
    <section className="fx fx-wrap-wrap gap-4">
      {items?.map(item => (
        <VVCCard
          key={item.id}
          video={item}
          screen={screen}
          isPending={!isCompanyVerified}
          onChange={(values) => item.id && _loadMoreService.updateOneItem(item.id, values)}
          onReplaceVideo={onRecordVideo}
        />
      ))}
      <div ref={refLoadMore}/>
    </section>
  )
}
